import type {
  LeagueStandingEntry,
  PlayerAnalytics,
  PlayerAnalyticsQuery,
  PlayerSeasonStats,
  ReplaceStandingsInput,
  SeasonExportQuery,
  SeasonQuery,
  SeasonStatsResponse,
  SeasonSummary,
  StandingsSyncResult,
  TeamAnalytics,
} from '@/types';
import api from './client';
import { getV2Error, V2ApiError, V2DetailResponse } from './v2';

export type SeasonListResponse = V2DetailResponse<SeasonSummary[]>;
export type SeasonOverviewResponse = V2DetailResponse<SeasonStatsResponse>;
export type StandingsResponse = V2DetailResponse<LeagueStandingEntry[]>;
export type TeamAnalyticsResponse = V2DetailResponse<TeamAnalytics>;
export type PlayerAnalyticsResponse = V2DetailResponse<PlayerAnalytics>;

const scoped = (workspaceId: string, signal?: AbortSignal) => ({
  headers: { 'X-Workspace-ID': workspaceId },
  signal,
});

const queryParams = (query: SeasonQuery): Record<string, string | number> => ({
  season: query.season,
  ...(query.from ? { from: query.from } : {}),
  ...(query.to ? { to: query.to } : {}),
});

const mappedError = async (error: unknown): Promise<never> => {
  if ((error as { code?: string })?.code === 'ERR_CANCELED') {
    const canceled: Error & V2ApiError = Object.assign(new Error('Request canceled.'), { code: 'canceled', details: {}, requestId: '' });
    throw canceled;
  }
  const response = (error as { response?: { data?: unknown } })?.response;
  if (response?.data instanceof Blob) {
    try {
      const parsed = JSON.parse(await response.data.text());
      throw getV2Error({ response: { data: parsed } });
    } catch (parseError) {
      if (typeof (parseError as Partial<V2ApiError>)?.code === 'string') throw parseError;
    }
  }
  throw getV2Error(error);
};

const safeFilename = (header: unknown, fallback: string): string => {
  if (typeof header !== 'string') return fallback;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = header.match(/filename="([^"]+)"/i)?.[1];
  let candidate = quoted;
  if (encoded) {
    try { candidate = decodeURIComponent(encoded); } catch { return fallback; }
  }
  if (!candidate) return fallback;
  candidate = candidate.split(/[\\/]/).pop() ?? '';
  return /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,199}\.csv$/.test(candidate) ? candidate : fallback;
};

export const seasonAPI = {
  getSeasons: async (workspaceId: string, signal?: AbortSignal): Promise<SeasonListResponse> => {
    try { return (await api.get('/v2/season/seasons', scoped(workspaceId, signal))).data; } catch (error) { return mappedError(error); }
  },
  getOverview: async (workspaceId: string, query: SeasonQuery, signal?: AbortSignal): Promise<SeasonOverviewResponse> => {
    try { return (await api.get('/v2/season/overview', { ...scoped(workspaceId, signal), params: queryParams(query) })).data; } catch (error) { return mappedError(error); }
  },
  getStandings: async (workspaceId: string, query: SeasonQuery, signal?: AbortSignal): Promise<StandingsResponse> => {
    try { return (await api.get('/v2/season/standings', { ...scoped(workspaceId, signal), params: { season: query.season } })).data; } catch (error) { return mappedError(error); }
  },
  getTeamAnalytics: async (workspaceId: string, query: SeasonQuery, signal?: AbortSignal): Promise<TeamAnalyticsResponse> => {
    try { return (await api.get('/v2/season/analytics/team', { ...scoped(workspaceId, signal), params: queryParams(query) })).data; } catch (error) { return mappedError(error); }
  },
  getPlayerAnalytics: async (workspaceId: string, query: PlayerAnalyticsQuery, signal?: AbortSignal): Promise<PlayerAnalyticsResponse> => {
    try { return (await api.get('/v2/season/analytics/players', { ...scoped(workspaceId, signal), params: { ...queryParams(query), ...(query.player ? { player: query.player } : {}) } })).data; } catch (error) { return mappedError(error); }
  },
  replaceStandings: async (workspaceId: string, input: ReplaceStandingsInput, signal?: AbortSignal): Promise<StandingsResponse> => {
    const entries = input.entries.map(({ teamName, position, played, won, drawn, lost, goalsFor, goalsAgainst, goalDifference, points, form, notes }) => ({ teamName, position, played, won, drawn, lost, goalsFor, goalsAgainst, goalDifference, points, ...(form ? { form } : {}), ...(notes ? { notes } : {}) }));
    try { return (await api.put('/v2/season/standings', { season: input.season, entries }, scoped(workspaceId, signal))).data; } catch (error) { return mappedError(error); }
  },
  syncStandings: async (workspaceId: string, season: number, signal?: AbortSignal): Promise<V2DetailResponse<StandingsSyncResult>> => {
    try { return (await api.post('/v2/season/standings/sync', undefined, { ...scoped(workspaceId, signal), params: { season } })).data; } catch (error) { return mappedError(error); }
  },
  exportDataset: async (workspaceId: string, query: SeasonExportQuery, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> => {
    const fallback = `season-${query.season}-${query.dataset}.csv`;
    try {
      const response = await api.get('/v2/season/export', { ...scoped(workspaceId, signal), params: { dataset: query.dataset, ...queryParams(query) }, responseType: 'blob' });
      return { blob: response.data, filename: safeFilename(response.headers['content-disposition'], fallback) };
    } catch (error) { return mappedError(error); }
  },
};

// Kept for any Phase 5 compatibility work; the migrated /season route does not use it.
export const legacySeasonAPI = {
  getSeasons: async (): Promise<{ data: SeasonSummary[] }> => (await api.get('/season/seasons')).data,
  getSeasonStats: async (params?: { seasonId?: number; fromDate?: string; toDate?: string }): Promise<{ data: SeasonStatsResponse }> => (await api.get('/season/stats', { params })).data,
  getSeasonPlayerStats: async (params?: { seasonId?: number; fromDate?: string; toDate?: string }): Promise<{ data: PlayerSeasonStats[] }> => (await api.get('/season/player-stats', { params })).data,
  getLeagueStandings: async (seasonId: number): Promise<{ data: LeagueStandingEntry[] }> => (await api.get('/season/standings', { params: { seasonId } })).data,
  saveLeagueStandings: async (seasonId: number, entries: LeagueStandingEntry[]): Promise<void> => { await api.post('/season/standings', { seasonId, entries }); },
  deleteStanding: async (id: number): Promise<void> => { await api.delete(`/season/standings/${id}`); },
  syncStandings: async (seasonId: number): Promise<{ message: string; teamsCount: number }> => (await api.post('/season/standings/sync', undefined, { params: { seasonId } })).data,
};
